import { useState, useEffect, useCallback, useMemo } from 'react';
import { Autocomplete, TextField, Box, CircularProgress, Typography, Chip } from '@mui/material';
import { useInput } from 'react-admin';
import { useDebounce } from '../../hooks/useDebounce';

interface OFOSearchResult {
  code: string;
  description: string;
  majorGroup: string;
  skillLevel: string;
  tasks: string[];
  alternateTitles: string[];
}

interface OfoCodeAutocompleteProps {
  source: string;
  label?: string;
  placeholder?: string;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
}

export function OfoCodeAutocomplete({
  source,
  label = 'OFO Code',
  placeholder = 'Search by code or description...',
  fullWidth = true,
  size = 'small',
}: OfoCodeAutocompleteProps) {
  const [options, setOptions] = useState<OFOSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [selectedValue, setSelectedValue] = useState<OFOSearchResult | null>(null);
  const debouncedSearch = useDebounce(inputValue, 300);

  const {
    field,
    fieldState: { error },
  } = useInput({ source });

  // Sync selected value with field value
  useEffect(() => {
    if (!field.value) {
      setSelectedValue(null);
      return;
    }
    // If field has a value but no selected option, fetch it
    if (selectedValue?.code !== field.value) {
      fetch(`/api/v1/ofo-codes/search?q=${encodeURIComponent(field.value)}&limit=1`)
        .then((res) => res.json())
        .then((data: OFOSearchResult[]) => {
          const match = data.find((r) => r.code === field.value);
          if (match) setSelectedValue(match);
        })
        .catch(() => {});
    }
  }, [field.value]);

  const fetchOptions = useCallback(async (query: string) => {
    if (query.length < 2) {
      setOptions([]);
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`/api/v1/ofo-codes/search?q=${encodeURIComponent(query)}&limit=20`);
      const data: OFOSearchResult[] = await res.json();
      setOptions(data);
    } catch (err) {
      console.error('Failed to search OFO codes', err);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOptions(debouncedSearch);
  }, [debouncedSearch, fetchOptions]);

  const handleChange = useCallback((_event: React.SyntheticEvent, value: OFOSearchResult | null) => {
    setSelectedValue(value);
    field.onChange(value ? value.code : '');
  }, [field]);

  return (
    <Box sx={{ width: '100%' }}>
      <Autocomplete
        options={options}
        loading={loading}
        inputValue={inputValue}
        value={selectedValue}
        getOptionLabel={(o) => `${o.code} - ${o.description}`}
        isOptionEqualToValue={(a, b) => a.code === b.code}
        onChange={handleChange}
        onInputChange={(_event, v) => setInputValue(v)}
        noOptionsText={
          inputValue.length < 2
            ? 'Type at least 2 characters to search'
            : 'No matching OFO codes found'
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            error={!!error}
            helperText={error?.message}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => {
          const { key, ...rest } = props;
          return (
            <Box
              component="li"
              key={key}
              {...rest}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                py: 1,
                px: 2,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {option.code}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {option.description}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                <Chip
                  label={option.majorGroup}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={option.skillLevel}
                  size="small"
                  color={option.skillLevel === 'High' ? 'success' : option.skillLevel === 'Medium' ? 'warning' : 'default'}
                  variant="outlined"
                />
              </Box>
            </Box>
          );
        }}
        fullWidth={fullWidth}
        size={size}
        sx={{ width: '100%' }}
      />
    </Box>
  );
}
